/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import debounce from 'lodash.debounce';
import SECRETS from './secrets';
import AppBar from './Components/AppBar';
import COLORS from './Components/styles/Colors';
import '@fontsource/roboto-mono/500.css';

const pageStyle = {
  height: '100vh',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'auto 1fr',
};

const StyledTable = styled(Table)(() => ({
  borderRadius: 0,
  '& .MuiTableCell-head': {
    color: COLORS.SLATE50,
    backgroundColor: COLORS.SLATE700,
    borderRadius: 0,
    padding: '0.25rem 0.875rem',
  },
  '& .MuiTableCell-body': { padding: '0.25rem 0.875rem' },
}));

const StyledBoxLoader = styled(Box)(() => ({
  backgroundColor: 'white',
  position: 'absolute',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  zIndex: 50,
}));

const StyledTypoLoader = styled(Typography)(() => ({
  fontFamily: '"Roboto Mono", monospace',
  marginTop: '0.875rem',
  fontSize: 20,
  color: `${COLORS.SLATE500}`,
}));

const removeTilde = (text) => (text ? text.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '');

const convertToShortLocalTime = (isoString) => {
  const date = new Date(isoString);
  return date.toTimeString().split(' ')[0].substring(0, 5);
};

const RutaScreen = ({
  isLogged,
  token,
  logOut,
  secondary,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  const [rutas, setRutas] = useState([]);
  const [dateFilter, setDateFilter] = useState(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [searchBarTextDisplay, setSearchBarTextDisplay] = useState('');
  const [filterString, setFilterString] = useState('');
  const [filtroRutas, setFiltroRutas] = useState([]);

  useEffect(() => {
    if (secondary === 'false') return;
    navigate('/');
  }, [secondary, navigate]);

  useEffect(() => {
    const loadRutas = async () => {
      axios.defaults.headers.common.Authorization = `bearer ${token}`;
      axios.defaults.baseURL = SECRETS.SERVERURL;
      try {
        const res = await axios.get('/controlruta');
        setRutas(res.data);
      } catch (e) {
        if (e.response?.status === 401) logOut();
      }
    };
    loadRutas();
  }, [token, logOut]);

  useEffect(() => {
    const loadData = async () => {
      setData(undefined);
      axios.defaults.headers.common.Authorization = `bearer ${token}`;
      axios.defaults.baseURL = SECRETS.SERVERURL;
      try {
        const [respRecorridos, respUnidades] = await Promise.all([
          axios.get(`/controlrecorrido/byorganizacion/${dateFilter}`),
          axios.get('/unidades/organizacion'),
        ]);
        const unitsMap = respUnidades.data.reduce((acc, u) => ({
          ...acc,
          [u.idUnidad]: u,
        }), {});
        const filtered = respRecorridos.data
          .filter((r) => r.terminado && !r.cancelado && unitsMap[r.idUnidad])
          .map((r) => ({
            id: r.id,
            route: r.nombreRuta,
            unit: unitsMap[r.idUnidad].unidad,
            operator: r.nombreOperador,
            start: convertToShortLocalTime(r.inicio),
            routeId: r.idControlRuta,
            rawStart: r.inicio,
          }))
          .sort((a, b) => a.operator.localeCompare(b.operator) || new Date(a.rawStart) - new Date(b.rawStart));
        setData(filtered);
      } catch (e) {
        console.error('Error cargando datos', e);
        setData([]);
      }
    };
    if (dateFilter) loadData();
  }, [dateFilter, token]);

  const debouncedFilter = useCallback(debounce((query) => {
    setFilterString(query);
  }, 500), []);

  const handleSearch = (val) => {
    setSearchBarTextDisplay(val);
    debouncedFilter(val);
  };

  const itemsFiltrados = (data || []).filter((row) => {
    const searchLow = removeTilde(filterString.toLowerCase());
    const passText = !filterString
      || removeTilde(row.route.toLowerCase()).includes(searchLow)
      || removeTilde(row.unit.toLowerCase()).includes(searchLow)
      || removeTilde(row.operator.toLowerCase()).includes(searchLow);
    const passRoute = filtroRutas.length === 0 || filtroRutas.includes(row.routeId);
    return passText && passRoute;
  });

  if (isLogged !== 'true') return <Navigate to="/" replace />;

  return (
    <div style={pageStyle}>
      <AppBar
        location="Salidas"
        secondary={secondary}
        handleLogOut={logOut}
        searchBarTextDisplay={searchBarTextDisplay}
        handleSearch={handleSearch}
        dateFilter={dateFilter}
        handleDateFilter={setDateFilter}
        rutas={rutas}
        filterRoutes={filtroRutas}
        handleFilterRoutes={setFiltroRutas}
        visibleStateRows={[]}
        handleStateRow={false}
        isVisibleNewTracksLength={false}
      />
      <Paper sx={{
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        borderRadius: 0,
      }}
      >
        {data === undefined && (
          <StyledBoxLoader>
            <div className="loader-spinner" />
            <StyledTypoLoader>Cargando Salidas...</StyledTypoLoader>
          </StyledBoxLoader>
        )}
        {data !== undefined && (
          <TableContainer sx={{ height: '100%' }}>
            <StyledTable stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Ruta</TableCell>
                  <TableCell align="center">Hora Salida</TableCell>
                  <TableCell align="center">Unidad</TableCell>
                  <TableCell align="center">Operador</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itemsFiltrados.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell align="center">{row.route}</TableCell>
                    <TableCell align="center">{row.start}</TableCell>
                    <TableCell align="center">{row.unit}</TableCell>
                    <TableCell align="center">{row.operator}</TableCell>
                  </TableRow>
                ))}
                {itemsFiltrados.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                      <Typography color="textSecondary">
                        No se encontraron registros con los filtros seleccionados.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </StyledTable>
          </TableContainer>
        )}
      </Paper>
    </div>
  );
};

export default RutaScreen;
